import { Component, EventEmitter, Output, ViewChild, ElementRef, OnDestroy, Input } from '@angular/core';
import { BrowserMultiFormatReader } from '@zxing/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';

// Type declarations for native BarcodeDetector API
declare global {
  interface Window {
    BarcodeDetector?: {
      // eslint-disable-next-line @typescript-eslint/prefer-function-type
      new (options: { formats: string[] }): BarcodeDetector;
    };
  }
}

interface BarcodeDetectorResult {
  rawValue: string;
  format: string;
}

interface BarcodeDetector {
  detect(source: HTMLVideoElement | HTMLCanvasElement): Promise<BarcodeDetectorResult[]>;
}

@Component({
  selector: 'app-qr-scanner',
  templateUrl: './qr-scanner.component.html',
  styleUrls: ['./qr-scanner.component.scss'],
})
export class QrScannerComponent implements OnDestroy {
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  @Output() code = new EventEmitter<string>();
  @Output() scanError = new EventEmitter<string>();

  @Input() showFileUpload = true;
  @Input() showFramingGuide = true;

  // Scanning mode properties
  scanningMode: 'qr' | 'barcode' | 'both' = 'both';

  isScanning = false;
  isLoading = false;
  hasCamera = false;
  lastScannedCode = '';
  lastScannedTime = 0;

  private codeReader: BrowserMultiFormatReader | null = null;
  private stream: MediaStream | null = null;
  private scanningInterval: ReturnType<typeof setInterval> | null = null;
  private barcodeDetector: BarcodeDetector | null = null;

  // Debounce settings
  private readonly DEBOUNCE_MS = 1000;

  constructor(private translate: TranslateService) {
    this.checkCameraAvailability();
    this.initializeBarcodeDetector();
  }

  private async checkCameraAvailability(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.hasCamera = devices.some((device) => device.kind === 'videoinput');
    } catch {
      console.warn('Could not check camera availability');
      this.hasCamera = false;
    }
  }

  private initializeBarcodeDetector(): void {
    // Check if native BarcodeDetector is available
    if (window.BarcodeDetector) {
      try {
        // Initialize with basic formats, will be updated based on scanning mode
        this.barcodeDetector = new window.BarcodeDetector({
          formats: this.getScanningFormats(),
        });
      } catch {
        console.warn('BarcodeDetector initialization failed');
        this.barcodeDetector = null;
      }
    }
  }

  private getScanningFormats(): string[] {
    switch (this.scanningMode) {
      case 'qr':
        return ['qr_code'];
      case 'barcode':
        return ['code_128', 'code_39', 'code_93', 'ean_8', 'ean_13', 'upc_a', 'upc_e', 'codabar', 'itf'];
      case 'both':
      default:
        return ['qr_code', 'code_128', 'code_39', 'code_93', 'ean_8', 'ean_13', 'upc_a', 'upc_e', 'codabar', 'itf'];
    }
  }

  async startScanning(): Promise<void> {
    if (!this.hasCamera) {
      this.scanError.emit(this.translate.instant('QRScanner_NoCameraFound') || 'No camera found on this device');
      return;
    }

    this.isLoading = true;

    try {
      // Request camera permissions
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (this.videoElement?.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;
        // Evitar error "Trying to play video that is already playing"
        if (this.videoElement.nativeElement.paused) {
          await this.videoElement.nativeElement.play();
        }

        this.isScanning = true;
        this.isLoading = false;

        // Start scanning using preferred method
        if (this.barcodeDetector) {
          this.startNativeDetection();
        } else {
          this.startZXingDetection();
        }
      }
    } catch (error) {
      this.isLoading = false;
      this.handleCameraError(error);
    }
  }

  private startNativeDetection(): void {
    if (!this.barcodeDetector) {
      this.barcodeDetector = new window.BarcodeDetector!({
        formats: this.getScanningFormats(),
      });
    }

    this.scanningInterval = setInterval(async () => {
      if (!this.videoElement?.nativeElement || !this.isScanning || !this.barcodeDetector) {
        return;
      }

      try {
        const barcodes = await this.barcodeDetector.detect(this.videoElement.nativeElement);
        if (barcodes.length > 0) {
          this.handleScannedCode(barcodes[0].rawValue);
        }
      } catch {
        // Silently continue - scanning errors are common
      }
    }, 200); // Reduced frequency: 200ms instead of 100ms
  }

  private startZXingDetection(): void {
    this.codeReader = new BrowserMultiFormatReader();

    this.codeReader.decodeFromVideoDevice(
      null, // usar null para "default device"
      this.videoElement.nativeElement,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (result, err) => {
        if (result) {
          this.handleScannedCode(result.getText());
        }
        // err puede ser NotFoundException en frames vacíos, se ignora
      },
    );
  }

  private handleScannedCode(rawCode: string): void {
    const now = Date.now();

    // Debounce duplicate scans
    if (rawCode === this.lastScannedCode && now - this.lastScannedTime < this.DEBOUNCE_MS) {
      return;
    }

    this.lastScannedCode = rawCode;
    this.lastScannedTime = now;

    // Process the code according to requirements
    let processedCode = rawCode;

    // If the scanned value contains # characters, only emit the substring after the last #
    const lastHashIndex = rawCode.lastIndexOf('#');
    if (lastHashIndex !== -1 && lastHashIndex < rawCode.length - 1) {
      processedCode = rawCode.substring(lastHashIndex + 1);
    }

    // Use setTimeout to prevent blocking the main thread
    setTimeout(() => {
      this.code.emit(processedCode);
      const codeScannedTitle = this.translate.instant('QRScanner_CodeScanned') || 'Code Scanned';
      const scannedLabel = this.translate.instant('QRScanner_Scanned') || 'Scanned:';
      GlobalMessageUtil.info(codeScannedTitle, `${scannedLabel} ${processedCode}`);
    }, 0);
  }

  stopScanning(): void {
    this.isScanning = false;
    this.isLoading = false;

    // Clear scanning interval
    if (this.scanningInterval) {
      clearInterval(this.scanningInterval);
      this.scanningInterval = null;
    }

    // Stop ZXing reader
    if (this.codeReader) {
      this.codeReader.reset();
      this.codeReader = null;
    }

    // Release camera stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    // Clear video element
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }
  }

  setScanningMode(mode: 'qr' | 'barcode' | 'both'): void {
    const wasScanning = this.isScanning;

    // Stop current scanning if active
    if (wasScanning) {
      this.stopScanning();
    }

    // Update mode
    this.scanningMode = mode;

    // Reset detector to use new formats
    if (this.barcodeDetector && window.BarcodeDetector) {
      this.barcodeDetector = new window.BarcodeDetector({
        formats: this.getScanningFormats(),
      });
    }

    // Restart scanning if it was active
    if (wasScanning && this.hasCamera) {
      setTimeout(() => {
        this.startScanning();
      }, 100);
    }

    const modeChangedTitle = this.translate.instant('QRScanner_ModeChanged') || 'Mode Changed';
    let modeMessage = '';

    switch (mode) {
      case 'qr':
        modeMessage = this.translate.instant('QRScanner_NowScanningQRCodes') || 'Now scanning for: QR CODES';
        break;
      case 'barcode':
        modeMessage = this.translate.instant('QRScanner_NowScanningBarcodes') || 'Now scanning for: BARCODES';
        break;
      case 'both':
      default:
        modeMessage = this.translate.instant('QRScanner_NowScanningBoth') || 'Now scanning for: QR codes and barcodes';
        break;
    }

    GlobalMessageUtil.success(modeChangedTitle, modeMessage);
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.isLoading = true;

    try {
      if (this.barcodeDetector) {
        await this.scanImageWithNative(file);
      } else {
        await this.scanImageWithZXing(file);
      }
    } catch {
      this.scanError.emit('Failed to scan image');
      GlobalMessageUtil.error('Scan Error', 'Could not read barcode from image');
    } finally {
      this.isLoading = false;
      // Clear the file input
      input.value = '';
    }
  }

  private async scanImageWithNative(file: File): Promise<void> {
    if (!this.barcodeDetector) return;

    const imageElement = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    return new Promise((resolve, reject) => {
      imageElement.onload = async () => {
        canvas.width = imageElement.width;
        canvas.height = imageElement.height;
        ctx.drawImage(imageElement, 0, 0);

        try {
          const barcodes = await this.barcodeDetector!.detect(canvas);
          if (barcodes.length > 0) {
            this.handleScannedCode(barcodes[0].rawValue);
          } else {
            this.scanError.emit('No barcode found in image');
            GlobalMessageUtil.warn('No Code Found', 'No barcode or QR code found in the selected image');
          }
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      imageElement.onerror = () => reject(new Error('Failed to load image'));
      imageElement.src = URL.createObjectURL(file);
    });
  }

  private async scanImageWithZXing(file: File): Promise<void> {
    // Simple fallback - just show error message for file scanning with ZXing
    // This is a fallback when native BarcodeDetector is not available
    console.log('File scanning attempted with ZXing fallback:', file.name);
    const errorMessage = this.translate.instant('QRScanner_FileScanningNotSupported') || 'File scanning not supported in this browser';
    this.scanError.emit(errorMessage);

    const featureUnavailableTitle = this.translate.instant('QRScanner_FeatureUnavailable') || 'Feature Unavailable';
    const fallbackMessage =
      this.translate.instant('QRScanner_FileScanningRequiresNewerBrowser') ||
      'File scanning requires a newer browser. Please use camera scanning instead.';
    GlobalMessageUtil.warn(featureUnavailableTitle, fallbackMessage);
  }

  private handleCameraError(error: unknown): void {
    let errorMessage = this.translate.instant('QRScanner_CameraAccessFailed') || 'Camera access failed';

    if (error && typeof error === 'object' && 'name' in error) {
      const errorName = (error as { name: string }).name;

      if (errorName === 'NotAllowedError') {
        errorMessage =
          this.translate.instant('QRScanner_CameraPermissionDenied') ||
          'Camera permission denied. Please enable camera access in your browser settings.';
      } else if (errorName === 'NotFoundError') {
        errorMessage = this.translate.instant('QRScanner_NoCameraFound') || 'No camera found on this device';
      } else if (errorName === 'NotReadableError') {
        errorMessage = this.translate.instant('QRScanner_CameraInUse') || 'Camera is already in use by another application';
      } else if (errorName === 'OverconstrainedError') {
        errorMessage = this.translate.instant('QRScanner_CameraNotSupported') || 'Camera not supported on this device';
      }
    }

    this.scanError.emit(errorMessage);
    const cameraErrorTitle = this.translate.instant('QRScanner_CameraError') || 'Camera Error';
    GlobalMessageUtil.error(cameraErrorTitle, errorMessage);
  }

  triggerFileUpload(): void {
    this.fileInput?.nativeElement?.click();
  }

  ngOnDestroy(): void {
    this.stopScanning();
  }
}
